import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getDocumentEditView,
  getCategories,
  getSubCategories,
  updateDocument,
  getBundleList,
  createDocumentBundle,
  addDocumentToBundle,
  createDocumentVersion,
} from '../../endpoints/documents';
import {
  DocumentBundleListItem,
  DocumentUpdate,
  DocumentVersionCreate,
} from '../../types/document';
import { GenericResponse } from '../../types/generic';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const DocumentEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [showBundleDialog, setShowBundleDialog] = useState(false);

  const [showAddToBundleModal, setShowAddToBundleModal] = useState(false);
  const [bundleSearchTerm, setBundleSearchTerm] = useState('');
  const [availableBundles, setAvailableBundles] = useState<
    DocumentBundleListItem[]
  >([]);
  const [isLoadingBundles, setIsLoadingBundles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedBundleOrder, setSelectedBundleOrder] = useState<{
    [bundleId: string]: number;
  }>({});
  const [bundleFormData, setBundleFormData] = useState({
    name: '',
    description: '',
  });

  // Version form data
  const [versionFormData, setVersionFormData] = useState<DocumentVersionCreate>(
    {
      version: '',
      description: '',
    }
  );

  // Form state for editing
  const [editFormData, setEditFormData] = useState<DocumentUpdate>({
    name: '',
    description: '',
    category_ids: [],
    sub_category_ids: [],
    tags: [],
  });

  // Separate state for raw tag input to allow typing commas and spaces
  const [rawTagInput, setRawTagInput] = useState<string>('');

  // Fetch document data
  const {
    data: document,
    isLoading,
    error,
  } = useApiGet(['document-edit', id], () => getDocumentEditView(id!), {
    enabled: !!id,
  });

  // Fetch categories and sub-categories
  const { data: categories = [] } = useApiGet(['categories'], getCategories);
  const { data: subCategories = [] } = useApiGet(
    ['sub-categories'],
    getSubCategories
  );

  // Populate form data when document loads
  useEffect(() => {
    if (document) {
      setEditFormData({
        name: document.name,
        description: document.description || '',
        category_ids: document.categories.map((cat) => cat.id),
        sub_category_ids: document.sub_categories.map((subCat) => subCat.id),
        tags: document.tags,
      });
      // Also populate raw tag input
      setRawTagInput(document.tags.join(', '));
    }
  }, [document]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (field: keyof DocumentUpdate, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedCategories =
      typeof value === 'string' ? value.split(',') : value;
    handleInputChange('category_ids', selectedCategories);
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedSubCategories =
      typeof value === 'string' ? value.split(',') : value;
    handleInputChange('sub_category_ids', selectedSubCategories);
  };

  const removeCategory = (categoryId: string) => {
    const newCategories = editFormData.category_ids.filter(
      (id) => id !== categoryId
    );
    handleInputChange('category_ids', newCategories);

    // Also remove sub-categories that don't belong to remaining categories
    const remainingSubCategories = editFormData.sub_category_ids.filter(
      (subCatId) => {
        const subCat = subCategories.find((sc) => sc.id === subCatId);
        return subCat && newCategories.includes(subCat.category_id);
      }
    );
    handleInputChange('sub_category_ids', remainingSubCategories);
  };

  const removeSubCategory = (subCategoryId: string) => {
    const newSubCategories = editFormData.sub_category_ids.filter(
      (id) => id !== subCategoryId
    );
    handleInputChange('sub_category_ids', newSubCategories);
  };

  const handleTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRawTagInput(value);

    // Parse tags from the raw input for storage
    const parsedTags = value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    handleInputChange('tags', parsedTags);
  };

  // Update document mutation
  const { mutate: updateDocumentMutation, isPending: isUpdating } = useApiSend(
    (data: DocumentUpdate) => updateDocument(id!, data),
    (data) => {
      console.log('Document updated successfully:', data);
      setEditMode(false);
      // Show success message
      if (data.message) {
        alert(data.message);
      } else {
        alert('Document updated successfully!');
      }
      // Refresh the document data
      window.location.reload();
    },
    (error) => {
      console.error('Error updating document:', error);
      // Show error message to user
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Error updating document: ${errorMessage}`);
    },
    [['document-edit', id]]
  );

  // Create bundle mutation
  const { mutate: createBundleMutation, isPending: isCreatingBundle } =
    useApiSend(
      (data: { name: string; description: string }) =>
        createDocumentBundle({
          ...data,
          document_id: id!,
        }),
      (data) => {
        console.log('Bundle created successfully:', data);
        setShowBundleDialog(false);
        setBundleFormData({ name: '', description: '' });
        // Refresh the document data to show the new bundle
        window.location.reload();
      },
      (error) => {
        console.error('Error creating bundle:', error);
      },
      [['document-edit', id]]
    );

  // Create document version mutation
  const { mutate: createVersionMutation, isPending: isCreatingVersion } =
    useApiSend<GenericResponse>(
      (data: DocumentVersionCreate) => createDocumentVersion(id!, data),
      (data) => {
        console.log('Version created successfully:', data);
        setShowVersionDialog(false);
        setVersionFormData({ version: '', description: '' });

        // Show success message and stay on the current page
        if (data.message) {
          alert(data.message);
        } else {
          alert('Version created successfully!');
        }

        // Refresh the page to show the new version
        window.location.reload();
      },
      (error) => {
        console.error('Error creating version:', error);
        alert('Error creating version. Please try again.');
      },
      [['document-edit', id]]
    );

  const handleSave = () => {
    // Validate tags format before saving
    const invalidTags = editFormData.tags.filter((tag) => {
      // Check for tags with spaces (should be single words)
      if (tag.includes(' ')) {
        return true;
      }
      // Check for tags that are too long
      if (tag.length > 50) {
        return true;
      }
      // Check for tags with special characters (allow alphanumeric and hyphens)
      if (!/^[a-zA-Z0-9-]+$/.test(tag)) {
        return true;
      }
      return false;
    });

    if (invalidTags.length > 0) {
      alert(
        `Invalid tags: ${invalidTags.join(', ')}. Tags should be single words with only letters, numbers, and hyphens.`
      );
      return;
    }

    updateDocumentMutation(editFormData);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleCreateVersion = () => {
    setVersionFormData({ version: '', description: '' });
    setShowVersionDialog(true);
  };

  const handleCreateBundle = () => {
    setBundleFormData({ name: '', description: '' });
    setShowBundleDialog(true);
  };

  const handleVersionInputChange = (
    field: keyof DocumentVersionCreate,
    value: string
  ) => {
    setVersionFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateVersionSubmit = () => {
    if (versionFormData.version.trim()) {
      createVersionMutation(versionFormData);
    }
  };

  const handleDeleteBundle = (bundleId: string) => {
    // TODO: Implement delete bundle functionality
    console.log('Deleting bundle:', bundleId);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading document...</Typography>
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading document</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/library/documents"
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Typography variant="body2" color="text.primary">
          {document.name}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {document.name}
        </Typography>
        <Stack direction="row" spacing={1}>
          {editMode ? (
            <>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#0072CE',
                '&:hover': { backgroundColor: '#005a9e' },
              }}
              startIcon={<EditIcon />}
              onClick={handleEditToggle}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="document tabs"
        >
          <Tab label="Overview" />
          <Tab label="Versions" />
          <Tab label="Bundles" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Document Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Document Name"
                  value={editFormData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!editMode}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  disabled={!editMode}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Categories</InputLabel>
                  <Select
                    multiple
                    value={editFormData.category_ids}
                    onChange={handleCategoryChange}
                    input={<OutlinedInput label="Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const category = categories.find(
                            (cat) => cat.id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={category?.name || value}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                removeCategory(value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              deleteIcon={<CloseIcon />}
                              sx={{
                                '& .MuiChip-deleteIcon': {
                                  color: '#666',
                                  '&:hover': {
                                    color: '#333',
                                  },
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Sub-Categories</InputLabel>
                  <Select
                    multiple
                    value={editFormData.sub_category_ids}
                    onChange={handleSubCategoryChange}
                    input={<OutlinedInput label="Sub-Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const subCategory = subCategories.find(
                            (subCat) => subCat.id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={subCategory?.name || value}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                removeSubCategory(value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              deleteIcon={<CloseIcon />}
                              sx={{
                                '& .MuiChip-deleteIcon': {
                                  color: '#666',
                                  '&:hover': {
                                    color: '#333',
                                  },
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {subCategories.map((subCategory) => (
                      <MenuItem key={subCategory.id} value={subCategory.id}>
                        {subCategory.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tags"
                  value={rawTagInput}
                  onChange={handleTagsChange}
                  placeholder="Enter tags separated by commas (e.g., official, draft, internal)"
                  helperText="Tags help organize and categorize documents. Enter multiple tags separated by commas."
                  disabled={!editMode}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Document Versions</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateVersion}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            Add Version
          </Button>
        </Box>
        <Card>
          <CardContent>
            <List>
              {document.versions.map((versionObj, index) => (
                <ListItem
                  key={versionObj.id}
                  divider={index < document.versions.length - 1}
                >
                  <ListItemText
                    primary={`Version ${versionObj.version}`}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {versionObj.is_active && (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {versionObj.is_public && (
                          <Chip
                            label="Public"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {versionObj.is_draft && (
                          <Chip
                            label="Draft"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {!versionObj.is_active &&
                          !versionObj.is_public &&
                          !versionObj.is_draft && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/library/documents/${id}/versions/${versionObj.id}`
                            )
                          }
                          sx={{ color: '#FF6B35' }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" sx={{ color: '#00A859' }}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ color: '#D32F2F' }}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Document Bundles</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowAddToBundleModal(true)}
              sx={{
                color: '#00A859',
                borderColor: '#00A859',
                '&:hover': {
                  borderColor: '#008a4a',
                  backgroundColor: 'rgba(0, 168, 89, 0.04)',
                },
              }}
            >
              Add to Existing Bundle
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBundle}
              sx={{
                backgroundColor: '#00A859',
                '&:hover': { backgroundColor: '#008a4a' },
              }}
            >
              Create Bundle
            </Button>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {document.bundles.map((bundle) => (
            <Grid item xs={12} md={6} key={bundle.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h6" component="h3">
                      {bundle.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          sx={{ color: '#0072CE' }}
                          onClick={() =>
                            navigate(`/library/bundles/${bundle.id}`)
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={{ color: '#D32F2F' }}
                          onClick={() => handleDeleteBundle(bundle.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Version Dialog */}
      <Dialog
        open={showVersionDialog}
        onClose={() => setShowVersionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Version</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Version Number"
            value={versionFormData.version}
            onChange={(e) =>
              handleVersionInputChange('version', e.target.value)
            }
            placeholder="e.g., 1.1, 2024.2"
            sx={{ mb: 2, mt: 1 }}
            disabled={isCreatingVersion}
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={versionFormData.description}
            onChange={(e) =>
              handleVersionInputChange('description', e.target.value)
            }
            multiline
            rows={3}
            placeholder="What changed in this version?"
            disabled={isCreatingVersion}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowVersionDialog(false)}
            disabled={isCreatingVersion}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateVersionSubmit}
            disabled={!versionFormData.version.trim() || isCreatingVersion}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            {isCreatingVersion ? 'Creating...' : 'Create Version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Dialog */}
      <Dialog
        open={showBundleDialog}
        onClose={() => setShowBundleDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Bundle</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Bundle Name"
                value={bundleFormData.name}
                onChange={(e) =>
                  setBundleFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Enter bundle name..."
                disabled={isCreatingBundle}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={bundleFormData.description}
                onChange={(e) =>
                  setBundleFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                multiline
                rows={3}
                placeholder="Describe what this bundle contains..."
                disabled={isCreatingBundle}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowBundleDialog(false)}
            disabled={isCreatingBundle}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (bundleFormData.name.trim()) {
                createBundleMutation(bundleFormData);
              }
            }}
            disabled={isCreatingBundle || !bundleFormData.name.trim()}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            {isCreatingBundle ? 'Creating...' : 'Create Bundle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Existing Bundle Modal */}
      <Dialog
        open={showAddToBundleModal}
        onClose={() => setShowAddToBundleModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Document to Existing Bundle</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Bundles"
              placeholder="Search for bundles by name or description..."
              value={bundleSearchTerm}
              onChange={(e) => setBundleSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={async () => {
                setIsLoadingBundles(true);
                setHasSearched(true);
                try {
                  const bundles = await getBundleList(
                    bundleSearchTerm || undefined
                  );
                  setAvailableBundles(bundles);
                } catch (error) {
                  console.error('Error fetching bundles:', error);
                  setAvailableBundles([]);
                } finally {
                  setIsLoadingBundles(false);
                }
              }}
              disabled={isLoadingBundles}
            >
              {isLoadingBundles ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {isLoadingBundles ? (
            <Typography>Searching for bundles...</Typography>
          ) : hasSearched && availableBundles.length > 0 ? (
            <List>
              {availableBundles.map((bundle) => (
                <ListItem
                  key={bundle.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={bundle.name}
                    secondary={bundle.description || 'No description'}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="Order"
                      type="number"
                      size="small"
                      value={selectedBundleOrder[bundle.id] || ''}
                      onChange={(e) => {
                        const order = parseInt(e.target.value) || 0;
                        setSelectedBundleOrder((prev) => ({
                          ...prev,
                          [bundle.id]: order,
                        }));
                      }}
                      placeholder="0"
                      sx={{ width: 100 }}
                      inputProps={{ min: 0 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        const order = selectedBundleOrder[bundle.id] || 0;
                        if (order < 0) {
                          alert('Order must be a non-negative number');
                          return;
                        }

                        try {
                          await addDocumentToBundle(bundle.id, {
                            document_id: id!,
                            order: order,
                          });
                          console.log(
                            `Document ${id} added to bundle ${bundle.id} at order ${order}`
                          );
                          setShowAddToBundleModal(false);
                          // Refresh the document data to show the new bundle
                          window.location.reload();
                        } catch (error) {
                          console.error(
                            'Error adding document to bundle:',
                            error
                          );
                          alert(
                            'Failed to add document to bundle. Please try again.'
                          );
                        }
                      }}
                      disabled={
                        !selectedBundleOrder[bundle.id] &&
                        selectedBundleOrder[bundle.id] !== 0
                      }
                      sx={{
                        color: '#00A859',
                        borderColor: '#00A859',
                        '&:hover': {
                          borderColor: '#008a4a',
                          backgroundColor: 'rgba(0, 168, 89, 0.04)',
                        },
                      }}
                    >
                      Add to Bundle
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : hasSearched && bundleSearchTerm && !isLoadingBundles ? (
            <Typography color="text.secondary">
              No bundles found matching "{bundleSearchTerm}"
            </Typography>
          ) : hasSearched ? (
            <Typography color="text.secondary">
              No bundles found. Try adjusting your search terms.
            </Typography>
          ) : (
            <Typography color="text.secondary">
              Search for bundles to add this document to
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddToBundleModal(false);
              setHasSearched(false);
              setAvailableBundles([]);
              setBundleSearchTerm('');
              setSelectedBundleOrder({});
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentEdit;
